import { map, Observable } from 'rxjs';

import { HisWidgetBase } from './his-widget-base';

import * as _ from 'lodash';
import { HisQueryParamUtils } from '../utils/his-query-param-utils';
import { HisWidgetUtils } from '../utils/his-widget-utils';

export abstract class HisWidgetBaseSelect<T> extends HisWidgetBase<T> {

    showNullItem = false;
    showDropdownIcon: boolean;
    // minimum length of autocomplete input to trigger a search
    minLength: number;
    // maximum number or suggestions to show in autocomplete
    limit?: number;

    not = false;
    originalNot: boolean;
    showUnequalOption: boolean;

    constructor( options: any = {} ) {
        super( options );

        this.showNullItem = options.showNullItem;
        this.showDropdownIcon = options.showDropdownIcon;
        this.minLength = options.minLength || 1;
        this.limit = options.limit || null;

        this.not = options.not;
        this.originalNot = options.not;

        this.showUnequalOption =
            ( _.isNil( options.showUnequalOption ) || String( options.showUnequalOption ).includes( 'true' ) ) ? true : false;
        
        if ( HisWidgetUtils.isActualSelection( options.value ) ) {
            this.isActiveByPreset.set( true ); // preset by search config
        }
    }

    /**
     * Presets the input fields of this widget.
     * - If no presets are given, nothing is done to prevent overriding searchConfig presets.
     * - If presets are given, but no preset value is found for this widget, it will be reset
     *   to an undefined state, to prevent mixing with searchConfig presets.
     * @param presets - the presets to apply
     */
    public presetInputFields( presets: any ): void {
        if ( !presets ) {
            return;
        }

        let presetValue: any;
        if ( HisQueryParamUtils.isFlatObjectFormat( presets ) ) {
            presetValue = HisWidgetUtils.getPresetForWidget( this, presets );
        } else {
            presetValue = presets[this.getWidgetPresetKey()];
        }

        this.doPresetInputFields( presetValue, () => this.isActiveByPreset.set( true ) );
    }

    public isWidgetModified(): boolean {
        // console.log('his-search-save-params.component#isFormModified() #widget.value', widget.value);
        // console.log('his-search-save-params.component#isFormModified() #widget.originalValue', widget.originalValue);
        if ( !this.operatorsEquals( this.not, this.originalNot ) ) {
            return true;
        }

        const value = HisWidgetUtils.isActualSelection( this.value ) ? this.value : null;
        const originalValue = HisWidgetUtils.isActualSelection( this.originalValue ) ? this.originalValue : null;

        if ( JSON.stringify( value ) !== JSON.stringify( originalValue ) ) {
            return true;
        }
        return false;
    }

    /**
     * Presets the input fields of this widget with the given value(s).
     * @param presetValue - the concrete preset value for this widget
     * @param onPresetSuccess - a callback to execute after successfully applying a preset value; used to mark it as visible
     */
    protected abstract doPresetInputFields( presetValue: any, onPresetSuccess: () => void ): void;

    /**
     * Apparently search-/tree- & multi-select widgets expect '[]' appended to their id in presets,
     * so it is added here for all except 'dropdown' aka `his-widget-select`.
     */
    private getWidgetPresetKey() {
        const suffix = this.kind === 'dropdown' ? '' : '[]';
        return this.id + suffix;
    }

    /**
     * IMPORTANT: Should not be used outside of generic search context
     */
    public getWidgetValues( idsStr: string, isLidSearch?: boolean ): Observable<T> {
        return this.getWidgetValueDetail( idsStr, isLidSearch ).pipe( map( ( { value } ) => value ) );
    }

    /**
     * Retrieves the actual value of a widget based on the ids passed in.
     * @param idsStr the ids that are selected by this widget
     * @param isLidSearch 
     * @returns an object containing the retrieved `value`, as well as the flag `hasUnresolvedId` indicating that at least one id (or lid)
     * could not be resolved to an element.
     */
    public abstract getWidgetValueDetail( idsStr: string, isLidSearch?: boolean ): Observable<ValuesDetail<T>>;

    private operatorsEquals( op1: boolean, op2: boolean ): boolean {
        if ( op1 === null || op1 === undefined ) {
            op1 = false;
        }

        if ( op2 === null || op2 === undefined ) {
            op2 = false;
        }

        if ( op1 === op2 ) {
            return true;
        }
        return false;
    }

}

type ValuesDetail<T> = { value: T, hasUnresolvedId: boolean } & Record<string, any>;
